import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import type { QrDevice } from "@shared/qrCodes";

export interface QrCodeRow {
  id: string;
  code: string;
  /** The link the QR image encodes. */
  url: string;
  name: string;
  destinationUrl: string;
  contactId: string | null;
  contactName: string | null;
  companyId: string | null;
  companyName: string | null;
  createdByName: string | null;
  pausedAt: string | null;
  archivedAt: string | null;
  createdAt: string;
  totalScans: number;
  scans30d: number;
  unique30d: number;
  lastScanAt: string | null;
  /** 30 daily counts, oldest first, Central days. */
  daily30: number[];
}

export interface QrAnalytics {
  days: 30 | 90 | 365;
  dayKeys: string[];
  daily: number[];
  totals: { scans: number; unique: number; botScans: number };
  devices: { device: QrDevice; n: number }[];
  places: { place: string; n: number }[];
  networks: { org: string; n: number }[];
  recent: { scannedAt: string; device: QrDevice; place: string | null; org: string | null }[];
}

const LIST_KEY = ["/api/qr-codes"] as const;

export function useQrCodes(archived: boolean) {
  return useQuery<QrCodeRow[]>({
    queryKey: [...LIST_KEY, { archived }],
    queryFn: async () => (await apiRequest("GET", `/api/qr-codes${archived ? "?archived=1" : ""}`)).json(),
    // The app default is staleTime: Infinity. Scans arrive all day, so this
    // page refetches whenever it opens and polls while it stays open.
    refetchOnMount: "always",
    refetchInterval: 60_000,
  });
}

export function useQrAnalytics(id: string | null, days: 30 | 90 | 365) {
  return useQuery<QrAnalytics>({
    queryKey: [...LIST_KEY, id, "analytics", days],
    queryFn: async () => (await apiRequest("GET", `/api/qr-codes/${id}/analytics?days=${days}`)).json(),
    enabled: Boolean(id),
    refetchOnMount: "always",
    refetchInterval: 60_000,
  });
}

export function useCreateQrCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { name: string; destination: string; contactId?: string; companyId?: string }) =>
      (await apiRequest("POST", "/api/qr-codes", body)).json() as Promise<QrCodeRow>,
    onSuccess: () => qc.invalidateQueries({ queryKey: LIST_KEY }),
  });
}

export function useUpdateQrCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: { id: string; name?: string; destination?: string; paused?: boolean; archived?: boolean }) =>
      (await apiRequest("PATCH", `/api/qr-codes/${id}`, body)).json() as Promise<QrCodeRow>,
    onSuccess: () => qc.invalidateQueries({ queryKey: LIST_KEY }),
  });
}

/** "400: {\"error\":\"Links must start with https://\"}" -> "Links must start with https://" */
export function qrErrorMessage(e: unknown, fallback: string): string {
  const msg = e instanceof Error ? e.message : "";
  const body = msg.replace(/^\d{3}:\s*/, "");
  try {
    const parsed = JSON.parse(body);
    if (typeof parsed?.error === "string") return parsed.error;
  } catch {
    // not JSON
  }
  return body || fallback;
}
